import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area } from 'recharts';
import { Card, CardHeader, CardContent, Typography, Box } from '@mui/material';
import { TrendingDown } from '@mui/icons-material';
import Spinner from 'components/Spinner';
import styles from './PortfolioDrawdownAnalysis.module.sass';

// Constants
const PORTFOLIO_COLOR = '#73F6FF';
const BENCHMARK_COLOR = '#9F2DEF';
const HIGHWATER_COLOR = '#3B82F6';

const PortfolioDrawdownAnalysis = ({ data, height }) => {
  if (!data?.portfolio_values) return null;

  // Process data to include high water mark and drawdowns
  const chartDataWithDrawdowns = data.portfolio_values.reduce((acc, point, index, arr) => {
    const currentValue = point.quote;
    const previousHighWater = index > 0 ? Math.max(...arr.slice(0, index).map(p => p.quote)) : currentValue;
    const highWaterMark = Math.max(previousHighWater, currentValue);
    const drawdown = (highWaterMark - currentValue) / highWaterMark;
    
    acc.push({
      date: point.timestamp,
      displayDate: point.timestamp,
      Value: currentValue,
      highWaterMark: highWaterMark,
      drawdown: -drawdown,
      underwaterArea: drawdown > 0 ? currentValue : null
    });
    
    return acc;
  }, []);

  // Calculate Y-axis domain with padding
  const allValues = chartDataWithDrawdowns.flatMap(d => [d.Value, d.highWaterMark].filter(Boolean));
  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);
  const valueRange = maxValue - minValue;
  const padding = valueRange * 0.1; // 10% padding
  const yDomain = [minValue - padding, maxValue + padding];

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercentage = (value) => {
    if (!value && value !== 0) return '-';
    return `${(value * 100).toFixed(2)}%`;
  };

  const CustomTooltipContent = ({ active, payload, label }) => {
    if (!active || !payload) return null;
    
    // Format the date for display
    const displayDate = label;
    const parts = displayDate.split('-');
    const formattedDate = `${parseInt(parts[1])}/${parseInt(parts[2])}/${parts[0]}`;
    
    return (
      <div className={styles.customTooltip}>
        <div className={styles.label}>{formattedDate}</div>
        {payload.map((entry, index) => {
          if (entry.dataKey === 'underwaterArea') return null;
          return (
            <div key={index} className={styles.value} style={{ color: entry.color }}>
              {entry.name}: {entry.dataKey === 'drawdown' ? 
                formatPercentage(-entry.value) : 
                formatCurrency(entry.value)}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Box className={styles.container}>
      <Card className={styles.cyberCard}>
        <div className={styles.outperformerBadge}>
          <TrendingDown />
          Drawdown Analysis
        </div>
        
        <CardContent className={styles.cardContent}>
          <div className={styles.chartContainer}>
            <ResponsiveContainer>
              <LineChart data={chartDataWithDrawdowns} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="drawdownGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={BENCHMARK_COLOR} stopOpacity={0.2} />
                    <stop offset="100%" stopColor={BENCHMARK_COLOR} stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid 
                  strokeDasharray="3 3" 
                  stroke="rgba(115, 246, 255, 0.1)"
                />
                <XAxis 
                  dataKey="displayDate" 
                  stroke="rgba(115, 246, 255, 0.5)"
                  tick={{ fill: 'rgba(115, 246, 255, 0.8)', fontSize: 12 }}
                  tickFormatter={(value) => {
                    // Split YYYY-MM-DD and return MM/DD format
                    const parts = value.split('-');
                    return `${parseInt(parts[1])}/${parseInt(parts[2])}`;
                  }}
                  interval="preserveStartEnd"
                />
                <YAxis 
                  domain={yDomain}
                  tickFormatter={formatCurrency}
                  stroke="rgba(115, 246, 255, 0.5)"
                  tick={{ fill: 'rgba(115, 246, 255, 0.8)', fontSize: 12 }}
                />
                <Tooltip content={<CustomTooltipContent />} />
                <Legend 
                  wrapperStyle={{
                    color: 'rgba(115, 246, 255, 0.8)'
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="underwaterArea"
                  fill="url(#drawdownGradient)"
                  stroke="none"
                  fillOpacity={1}
                />
                <Line 
                  type="monotone" 
                  dataKey="highWaterMark"
                  name="High Water Mark"
                  stroke={HIGHWATER_COLOR}
                  strokeWidth={1}
                  dot={false}
                  strokeDasharray="3 3"
                />
                <Line 
                  type="monotone" 
                  dataKey="Value"
                  name="Portfolio Value"
                  stroke={PORTFOLIO_COLOR}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, stroke: PORTFOLIO_COLOR, strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className={styles.statsGrid}>
            <div className={styles.metricBox}>
              <div className={styles.metricLabel}>Maximum Drawdown</div>
              <div className={styles.metricContent}>
                <div className={styles.metricValue}>
                  {formatPercentage(data.max_drawdown)}
                </div>
                <div className={styles.metricSubtext}>Peak to Trough</div>
              </div>
            </div>
            
            <div className={styles.metricBox}>
              <div className={styles.metricLabel}>Days in Drawdown</div>
              <div className={styles.metricContent}>
                <div className={styles.metricValue}>
                  {formatPercentage(data.percentage_days_in_drawdown / 100)}
                </div>
                <div className={styles.metricSubtext}>Time Underwater</div>
              </div>
            </div>

            <div className={styles.metricBox}>
              <div className={styles.metricLabel}>Return to HWM</div>
              <div className={styles.metricContent}>
                <div className={styles.metricValue}>
                  {formatPercentage(data.return_to_breakeven / 100)}
                </div>
                <div className={styles.metricSubtext}>Required Recovery</div>
              </div>
            </div>
          </div>
          
          <div className={styles.insightBox}>
            <div className={styles.insightTitle}>Drawdown Insights</div>
            <div className={styles.insightContent}>
              <p>
                Your portfolio experienced a {formatPercentage(data.max_drawdown)} maximum drawdown from its peak value.
                To recover completely, a {formatPercentage(data.return_to_breakeven / 100)} gain is required to return to the previous high-water mark.
              </p>
              <p>
                The portfolio has spent {formatPercentage(data.percentage_days_in_drawdown / 100)} of the time below peak values, which is 
                {data.percentage_days_in_drawdown > 50 ? " higher than ideal and may indicate prolonged recovery periods." : " within normal range for portfolio volatility."} 
                {data.max_drawdown > 0.2 ? " Consider reviewing risk management strategies to mitigate future drawdowns." : ""}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PortfolioDrawdownAnalysis;